// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { max } from 'lodash-es';
import { DEFAULT_TRAJECTORY_FUNCTION_TYPE } from '../../app.constants';
import { getTransitionTargets } from '../../feature/dashboard/functions/get-transition-targets';
import { setUnsetParameters } from '../../functions/board-parameters/set-unset-parameters';
import { holdsInitialisedUtilisationTE } from '../../functions/client-model/client-node/holds-initialised-utilisation-te';
import { getTransitionYearsFromTimeline } from '../../functions/timeline/get-transition-years-from-timeline';
import { TrajectoryFunctionType } from '../../functions/transition-targets/trajectory-function-type';
import { arrayOfDictsToDictOfArrays } from '../../functions/utilities/array-of-dicts-to-dict-of-arrays';
import { ClientParameter } from '../../model/client-model/client-parameter';
import { Dict } from '../../model/dict';
import { ClientBoardContainer } from '../../public/dashboard/shared/model/client-board-container';
import { CccAdapterTransitionTargetItem } from '../climate-engine/ccc-adapter/ccc-adapter-transition-target-item';
import { CccAdapterService } from '../climate-engine/ccc-adapter/ccc-adapter.service';
import { TransitionData } from '../climate-engine/ccc-algorithm/transition-data';
import { CCCModelUtility } from '../climate-engine/ccc-model-utility/ccc-model-utility';

@Injectable({
  providedIn: 'root',
})
export class TargetedEmissionsService {
  constructor(
    private _cccAdapterService: CccAdapterService,
    private _cccModelUtility: CCCModelUtility
  ) {}
  /**
   * A faster version, is used because calculating targeted emissions for the full plan takes too much time.
   * Running only two passes instead of a pass for each te. One for shifts and then another for the non-shifts.
   * It will give slightly less emissions for non-shifts, but that is fine within the context it is being used now.
   *
   * @param board
   * @returns
   */
  calculateSimple(board: ClientBoardContainer): Dict<number[]> {
    const years = getTransitionYearsFromTimeline(board.boardSettings.timeline);
    const targets = this.getTargets(board);
    const transitionsDicts: Array<Dict<TransitionData>> = years.map((y) => ({}));
    const shifts = [];
    const nonShifts = [];
    targets.utilisation.forEach((t) => {
      if (this._cccModelUtility.isTransitionElementShift(t.transitionElement)) {
        shifts.push(t);
      } else {
        nonShifts.push(t);
      }
    });

    const runPass = (others: CccAdapterTransitionTargetItem[], adjusted: CccAdapterTransitionTargetItem[]) => {
      this._initCccAdapterService(board, [...others, ...adjusted], board.parameters);

      const transitions = years.map((year, t) => this._cccAdapterService.calculate(t).transitions);
      adjusted.forEach((target) => {
        const te = target.transitionElement;
        transitions.forEach((transition, index) => {
          const transitionsForYear = transitionsDicts[index];
          transitionsForYear[te] = transition[te];
        });
      });
    };

    const adjustedShifts = shifts.map((s) => this._getTargetAdjustedForSimpleTargetedEmissions(s, years));
    const adjustedNonShifts = nonShifts.map((n) => this._getTargetAdjustedForSimpleTargetedEmissions(n, years));

    runPass([...targets.supply, ...nonShifts], adjustedShifts);
    runPass([...targets.supply, ...shifts], adjustedNonShifts);
    return this._createTargetedEmissions(transitionsDicts);
  }

  private _createTargetedEmissions(transitionsDicts: Array<Dict<TransitionData>>): Dict<number[]> {
    const targetedEmissions = {};
    const transitions = arrayOfDictsToDictOfArrays(transitionsDicts);
    Object.entries(transitions).forEach(([id, transitionData]) => {
      const grossCarbonAbatement = transitionData.map((transition) =>
        this._getCarbonAbatement(transition.carbonAbatement)
      );
      const targetedByTe = [];
      for (let i = 0; i < transitionData.length; i++) {
        targetedByTe.push(grossCarbonAbatement[i]);
      }
      targetedEmissions[id] = targetedByTe;
    });
    return targetedEmissions;
  }

  private _getCarbonAbatement(carbonAbatement: { net?: number; gross?: number } = {}): number {
    const abatement = carbonAbatement.gross ?? carbonAbatement.net ?? 0;
    return isNaN(abatement) ? 0 : abatement;
  }

  private _getTargetAdjustedForTargetedEmissions(
    target: CccAdapterTransitionTargetItem,
    years: number[]
  ): CccAdapterTransitionTargetItem {
    //We want to ensure that we use the highest value among those set for a manual path. Otherwise we could end up with negative emissions towards the end of the plan.
    const maxValue = target.manualPath ? max(target.manualPath) : target.targetLevel;
    return {
      ...target,
      manualPath: years.map((y) => maxValue),
      trajectoryFunctionType: TrajectoryFunctionType.MANUAL,
    };
  }

  /**
   * Needed for the simple version since there the adjusted targets are run in parallel
   * so if adjusting the manual path to the max value for all we can end up in a situation
   * where the potential is higher in the actual plan than it is in the targeted emission scenario.
   * This will result in negative emissions in the dashboards when it occurs.
   * We don't want to do this for the regular version since we only want the other transitions to affect the curve.
   * Although arguably this could be needed to be done for the regular one as well, because it is nothing inherently wrong
   * with having the target value go down instead of up during a transition.
   * @param target
   * @param years
   * @returns
   */
  private _getTargetAdjustedForSimpleTargetedEmissions(
    target: CccAdapterTransitionTargetItem,
    years: number[]
  ): CccAdapterTransitionTargetItem {
    if (target.manualPath) {
      const manualPath = target.manualPath.reduceRight((previous, currentValue) => {
        const highValue = previous[0] ?? -Infinity;
        const value = currentValue > highValue ? currentValue : highValue;
        previous.unshift(value);
        return previous;
      }, []);
      return {
        ...target,
        manualPath,
        trajectoryFunctionType: TrajectoryFunctionType.MANUAL,
      };
    }
    return this._getTargetAdjustedForTargetedEmissions(target, years);
  }

  private getTargets(board: ClientBoardContainer): {
    utilisation: CccAdapterTransitionTargetItem[];
    supply: CccAdapterTransitionTargetItem[];
  } {
    const toTarget = (target) => {
      return {
        targetLevel: target.nodeProperties.transitionTargetData.transitionProperties.targetLevel,
        transitionElement: target.nodeProperties.transitionTargetData.transitionTargetProperties.transitionElement,
        trajectoryFunctionType:
          target.nodeProperties.transitionTargetData.transitionProperties.transitionPath?.type ||
          DEFAULT_TRAJECTORY_FUNCTION_TYPE,
        manualPath: target.nodeProperties.transitionTargetData.transitionProperties.transitionPath?.manualPath,
      };
    };
    return {
      utilisation: getTransitionTargets(board)
        .filter((t) => holdsInitialisedUtilisationTE(t))
        .map(toTarget),
      supply: getTransitionTargets(board)
        .filter((t) => !holdsInitialisedUtilisationTE(t))
        .map(toTarget),
    };
  }

  private _initCccAdapterService(
    board: ClientBoardContainer,
    transitionTargetItems: CccAdapterTransitionTargetItem[],
    parameters: ClientParameter[]
  ): void {
    this._cccAdapterService.init(
      { electricityEmissionFactorType: board.boardSettings.electricityEmissionFactorType },
      transitionTargetItems,
      setUnsetParameters(parameters),
      board.boardSettings.timeline,
      true
    );
  }
}
